import { createAsyncThunk, createSlice, Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
} from 'types/rest/IFetchStatus';
import { convertSerializedErrorToError } from './convertSerializedErrorToError';
import { IFetchDataState } from './IFetchDataState';

export const createAsyncSliceWithData = <Data>(
  name: string,
  action: () => Promise<Data>,
  defaultData: Data = {} as Data
) => {
  const asyncAction = createAsyncThunk(`${name}/fetchData`, action);
  const initialState = {
    data: defaultData,
    fetchError: undefined,
    fetchStatus: createDefaultFetch(),
    updateFetchError: undefined,
    updateFetchStatus: createDefaultFetch(),
    query: {},
  } as IFetchDataState<{}, Data>;
  const slice = createSlice<IFetchDataState<{}, Data>, SliceCaseReducers<IFetchDataState<{}, Data>>>({
    name,
    initialState,
    reducers: {
      reset: () => initialState,
      setData: (state, action: PayloadAction<Data>) => {
        if (!state.data || isEqual(state.data, initialState.data)) {
          state.fetchError = undefined;
          state.fetchStatus = createFetchedSuccess();
        } else {
          state.updateFetchError = undefined;
          state.updateFetchStatus = createFetchedSuccess();
        }
        state.data = action.payload as Draft<Data>;
      },
    },
    extraReducers: (builder) =>
      builder
        .addCase(asyncAction.pending, (state) => {
          if (!state.data || isEqual(state.data, initialState.data)) {
            state.fetchStatus = createFetchInProgress();
          } else {
            state.updateFetchStatus = createFetchInProgress();
          }
        })
        .addCase(asyncAction.fulfilled, (state, action: PayloadAction<Data>) => {
          if (!state.data || isEqual(state.data, initialState.data)) {
            state.fetchError = undefined;
            state.fetchStatus = createFetchedSuccess();
          } else {
            state.updateFetchError = undefined;
            state.updateFetchStatus = createFetchedSuccess();
          }
          state.data = action.payload as Draft<Data>;
        })
        .addCase(asyncAction.rejected, (state, action) => {
          const newError = convertSerializedErrorToError(action.error);
          if (!state.data || isEqual(state.data, initialState.data)) {
            state.fetchError = newError;
            state.fetchStatus = createFetchedWithError();
          } else {
            state.updateFetchError = newError;
            state.updateFetchStatus = createFetchedWithError();
          }
        }),
  });
  return { slice, asyncAction };
};
